import { useState, useEffect, useMemo, useCallback } from "react";
import { api, useAuth } from "../App";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import { toast } from "sonner";

/**
 * AdminTasksPage — queue for admin requests filed via AdminTaskWidget.
 * bug_7412e0f449fb. Lists all admin tasks with status filter, resolve /
 * re-open / delete actions. Mukesh (or whoever's assigned) works the queue
 * here. Anyone can VIEW; only requester / assignee / admin can mutate.
 */
export default function AdminTasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open"); // open | resolved | all

  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);
  const myUid = user?.user_id || user?.id || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const r = await api.get("/admin-tasks", { params });
      setTasks(r.data || []);
    } catch (e) {
      toast.error("Couldn't load admin tasks");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    // Always show counts for both buckets — fetch separately on filter change
    // would be cleaner but for ~hundreds of rows the filter-after-fetch pattern
    // is fine. We compute open vs resolved from the current list.
    const open = tasks.filter((t) => t.status === "open").length;
    const resolved = tasks.filter((t) => t.status === "resolved").length;
    return { open, resolved, total: tasks.length };
  }, [tasks]);

  const resolve = async (task, undo = false) => {
    try {
      await api.put(`/admin-tasks/${task.admin_task_id}`, {
        status: undo ? "open" : "resolved",
      });
      toast.success(undo ? "Re-opened" : "Marked resolved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`Delete "${task.title}"? Admin only — cannot be undone.`)) return;
    try {
      await api.delete(`/admin-tasks/${task.admin_task_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed (admin only)");
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  const fmtDuration = (start, end) => {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 0) return null;
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h`;
    return `${Math.round(hrs / 24)}d`;
  };

  const PRIORITY_COLORS = {
    Urgent: "#ef4444",
    High: "#f59e0b",
    Medium: "#3b82f6",
    Low: "#64748b",
  };

  return (
    <div className="max-w-[920px] mx-auto p-4 md:p-6">
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="font-heading text-[20px] text-[var(--t1)]">ADMIN TASKS</h1>
        <span className="text-xs text-[var(--t3)]">
          Ops requests routed to the admin manager
        </span>
      </div>
      <p className="text-[12px] text-[var(--t3)] mb-5 leading-relaxed">
        File a request via the orange <span style={{ color: "#f59e0b", fontWeight: 600 }}>📋 ADMIN TASK</span> button.
        Captures who/when on submit, who/when on resolve.
      </p>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { key: "open", label: "Open", count: counts.open },
          { key: "resolved", label: "Resolved", count: counts.resolved },
          { key: "all", label: "All", count: null },
        ].map((p) => {
          const active = statusFilter === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setStatusFilter(p.key)}
              className="text-xs"
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: active ? "rgba(245,158,11,0.15)" : "var(--s1)",
                border: `1px solid ${active ? "rgba(245,158,11,0.45)" : "var(--bdr)"}`,
                color: active ? "#f59e0b" : "var(--t2)",
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
              }}
            >
              {p.label}
              {p.count != null && (
                <span style={{ marginLeft: 6, opacity: 0.75 }}>· {p.count}</span>
              )}
            </button>
          );
        })}
        <button
          onClick={load}
          className="text-xs text-[var(--t3)] hover:text-[var(--t1)]"
          style={{ marginLeft: "auto", padding: "6px 8px" }}
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {loading ? (
        <div className="text-[var(--t3)] text-sm">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-[var(--bdr)] bg-[var(--s1)]">
          <p className="text-[var(--t2)] text-sm mb-1">
            {statusFilter === "open" ? "No open admin tasks 🎉" : "Nothing here"}
          </p>
          <p className="text-[var(--t3)] text-xs">
            File one via the orange button (bottom-right of any page).
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const isResolved = t.status === "resolved";
            const isMine = t.requester_user_id === myUid;
            const isAssignee = t.assignee_user_id === myUid;
            const canMutate = isAdmin || isMine || isAssignee;
            const turnaround = fmtDuration(t.created_at, t.resolved_at);
            const priorityColor = PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.Medium;
            return (
              <div
                key={t.admin_task_id}
                style={{
                  background: "var(--s1)",
                  border: "1px solid var(--bdr)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  opacity: isResolved ? 0.62 : 1,
                  transition: "opacity .15s",
                }}
              >
                <div className="flex items-start gap-3">
                  {/* Priority bar */}
                  <span
                    title={`Priority: ${t.priority || "Medium"}`}
                    style={{
                      width: 3, alignSelf: "stretch", borderRadius: 2,
                      background: priorityColor,
                      flexShrink: 0,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <h3
                        style={{
                          fontSize: 14, fontWeight: 600,
                          color: isResolved ? "var(--t3)" : "var(--t1)",
                          textDecoration: isResolved ? "line-through" : "none",
                          margin: 0,
                        }}
                      >
                        {t.title}
                      </h3>
                      {isResolved && (
                        <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 700, letterSpacing: ".06em" }}>
                          ✓ RESOLVED
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: priorityColor, fontWeight: 700, letterSpacing: ".06em" }}>
                        {(t.priority || "Medium").toUpperCase()}
                      </span>
                    </div>
                    {t.description && (
                      <p style={{
                        fontSize: 12.5, color: "var(--t2)",
                        margin: "0 0 8px 0", whiteSpace: "pre-wrap", lineHeight: 1.45,
                      }}>
                        {t.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 11, color: "var(--t3)" }}>
                      <span>
                        From <strong style={{ color: "var(--t2)" }}>{t.requester_name || "Unknown"}</strong>
                        {" "}· {fmtDateTime(t.created_at)}
                      </span>
                      <span style={{ color: "var(--t4, #444)" }}>·</span>
                      <span>
                        → <strong style={{ color: "var(--t2)" }}>{t.assignee_label || "Unassigned"}</strong>
                      </span>
                      {isResolved && t.resolved_by_name && (
                        <>
                          <span style={{ color: "var(--t4, #444)" }}>·</span>
                          <span>
                            Resolved by <strong style={{ color: "#22c55e" }}>{t.resolved_by_name}</strong>
                            {" "}· {fmtDateTime(t.resolved_at)}
                            {turnaround && (
                              <span style={{ marginLeft: 6, color: "var(--t3)" }}>({turnaround})</span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {canMutate && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!isResolved ? (
                        <button
                          onClick={() => resolve(t)}
                          style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 600,
                            background: "rgba(34,197,94,0.15)", color: "#22c55e",
                            border: "1px solid rgba(34,197,94,0.3)",
                            borderRadius: 6, cursor: "pointer",
                          }}
                          title="Mark resolved (stamps timestamp)"
                        >
                          ✓ Resolve
                        </button>
                      ) : (
                        <button
                          onClick={() => resolve(t, true)}
                          style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 600,
                            background: "transparent", color: "var(--t2)",
                            border: "1px solid var(--bdr)", borderRadius: 6, cursor: "pointer",
                          }}
                          title="Re-open this request"
                        >
                          ↺ Re-open
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => remove(t)}
                          style={{
                            padding: "5px 8px", fontSize: 11,
                            background: "transparent", color: "var(--t3)",
                            border: "1px solid var(--bdr)", borderRadius: 6, cursor: "pointer",
                          }}
                          title="Delete (admin only)"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
